import React, { useEffect, useState } from 'react';
import CleverLoginButton, { CleverLoginButtonType } from '../../components/Common/CleverLoginButton';
import 'react-phone-input-2/lib/style.css';
import { CleverAuthType } from '../../services/clever.service';
import { Link, useNavigate } from 'react-router-dom';
import { faEnvelope as faEnvelopeRegular } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BackButton from '../../components/Common/BackButton';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { handleTeacherGoogleSignUp } from './services/teacher-google-signup.service';
import Loader from '../../components/Common/Loader';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { setRedirectURL } from '../../store/userSlice';
import { getURLSearchParams } from '../../helper/helper';
import { UserRoleEnum } from '../../interfaces/user.interface';
import { resolveWpProjectIdFromProjectParam } from './services/wp-project.service';

const SignUpOptions = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigateTo = useNavigate();

  const [googleLoader, setGoogleLoader] = useState<boolean>(false);
  const userAuthorized = useSelector((state: RootState) => state.user.userAuthorized);
  const user = useSelector((state: RootState) => state.user.user);

  const searchParams = getURLSearchParams(true);
  const tallyEmail = searchParams.get('email') ?? '';
  const rawTallyProject = searchParams.get('project') ?? '';
  const tallyLeadId = searchParams.get('lead_id') ?? '';

  const [tallyProject, setTallyProject] = useState<string>(rawTallyProject);

  useEffect(() => {
    const resolveProject = async () => {
      if (rawTallyProject) {
        const resolvedId = await resolveWpProjectIdFromProjectParam(rawTallyProject);
        if (resolvedId) {
          setTallyProject(resolvedId);
          localStorage.setItem('tally_project', resolvedId);
        }
      }
    };
    resolveProject();
    if (tallyLeadId) localStorage.setItem('tally_lead_id', tallyLeadId);
  }, [rawTallyProject, tallyLeadId]);

  useEffect(() => {
    if (user && userAuthorized) {
      dispatch(
        setRedirectURL(user.role === UserRoleEnum.teacher ? '/classroom/dashboard' : '/'),
      );
    }
  }, [dispatch, user, userAuthorized]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sofiaSans justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:max-w-sm sm:mx-auto h-full min-h-full flex flex-1 flex-col justify-between">
        <div></div>
        <div className="sm:mx-auto sm:w-full sm:max-w-sm py-8 px-4 sm:px-12">
          <div className="text-left mb-6">
            <h1 className="text-3xl font-bold text-black mb-3">Teachers: foster a class that loves stories</h1>
            <p className="text-black text-lg">
              Set up your class in under 5 minutes. Get started by signing up or signing in. You can get your students
              creating stories right away.
            </p>
          </div>

          {/* Google Sign Up Button */}
          <div className="mb-4">
            <button
              disabled={googleLoader}
              onClick={() => handleTeacherGoogleSignUp(setGoogleLoader, dispatch, navigateTo, 'teacher', tallyProject, tallyLeadId)}
              className="w-full flex justify-center gap-3 bg-secondaryStoryOptionalLightBlueText text-white font-bold font-sofiaSans rounded-lg px-4 py-2 text-base items-center cursor-pointer border border-gray-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FontAwesomeIcon
                title="Sign Up with Google"
                icon={faGoogle}
                className="text-xl text-white cursor-pointer"
              />
              Continue with Google
              <Loader loader={googleLoader} />
            </button>
          </div>

          <div className="mb-4">
            <CleverLoginButton
              redirectUrl="/"
              authType={CleverAuthType.TEACHER_SIGN_UP}
              type={CleverLoginButtonType.BORDER_SIGN_IN}
              className="text-base font-bold font-sofiaSans rounded-lg px-4 py-1.5"
              title="Sign Up with Clever"
            >
              Continue with Clever
            </CleverLoginButton>
          </div>

          <div className="mb-4">
            <Link
              to={`/auth/teacher/signup${(() => {
                const params = new URLSearchParams();
                if (tallyEmail) params.set('email', tallyEmail);
                if (tallyProject) params.set('project', tallyProject);
                if (tallyLeadId) params.set('lead_id', tallyLeadId);
                const qs = params.toString();
                return qs ? `?${qs}` : '';
              })()}`}
              className={`
              w-full flex items-center justify-center border border-secondaryStoryGrayBorder font-sofiaSans hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors
              text-secondaryStoryBlackText text-base font-bold rounded-lg px-4 py-2
            `}
            >
              <div className="flex items-center font-bold text-base">
                <div className="pr-2 flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={faEnvelopeRegular as IconProp}
                    className={`text-secondaryStoryBlackText text-2xl `}
                  />
                </div>
                <span>{'Continue with Email'}</span>
              </div>
            </Link>
          </div>

          <div>
            <Link
              to="/auth/teacher/login"
              className={`
              w-full flex items-center justify-center border border-secondaryStoryGrayBorder font-sofiaSans hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors
              text-secondaryStoryBlackText text-base font-bold rounded-lg px-4 py-2
            `}
            >
              <div className="flex items-center font-bold text-base">
                <span>Log in</span>
              </div>
            </Link>
          </div>

          <div className="flex items-center justify-between mt-6">
            <div>
              <BackButton onClick={() => window.history.back()}></BackButton>
            </div>
            <img src="/new_black_logo.png" alt="Build My Story" className="h-6" />
          </div>
        </div>

        <div className="sm:max-w-sm sm:mx-auto text-center text-base text-black">
          <Link to="/auth" className="underline">
            Go to Parent Login / Sign Up
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignUpOptions;
